/**
 * Leviathan design tokens.
 *
 * Source of truth for values that cannot live cleanly in CSS custom
 * properties: motion timing, easing curves, z-index tiers, elevation
 * specs, typography weight + tracking tables.
 *
 * CSS-expressible tokens (colors, radii, font families) live in
 * `src/index.css` `@theme inline {}`. This file mirrors them as
 * constants so TypeScript consumers (motion presets, inline styles,
 * computed animations) have a single import.
 *
 * Do not add em-dashes anywhere.
 */

export const color = {
  blackRich: '#000000',
  blackElevated: '#060608',
  blackHover: '#10101A',
  grayBorder: '#161620',
  grayMuted: '#2A2A3A',
  graySecondary: '#6B6B80',
  grayPrimary: '#A0A0B4',
  whiteSoft: '#E0E0EC',
  whitePure: '#FFFFFF',
  goldDim: '#8B7A3C',
  gold: '#C9A84C',
  goldBright: '#E8C84A',
  purpleDim: '#4A2D7A',
  purple: '#7C3AED',
  purpleBright: '#A78BFA',
  teal: '#14B8A6',
  tealDim: '#0F766E',
  slate: '#64748B',
  destructive: '#DC2626',
  destructiveSoft: '#FCA5A5',
  positive: '#10B981',
  // Chart-semantic colors (factor-model bars, sparklines, axis grids).
  // Referenced when fixed-meaning accents are needed beyond pillar hues.
  // Authored by WS-4; kept as the canonical name for these hues.
  chartBlue: '#60A5FA',
  chartAmber: '#F59E0B',
  // Chart-specific surfaces and gridlines (WS-3). Used by recharts
  // CartesianGrid/Tooltip on dashboard and M&A charts.
  chartGrid: '#1E3A5F',
  chartGridDim: '#334155',
  chartPanel: '#1e293b',
  chartAxis: '#94A3B8',
  chartEmerald: '#34D399',
  // Deep page / grid cell background used when contrast-tinted text requires
  // a darker-than-bgElevated surface (e.g. correlation heatmap low-abs cells).
  bgDeep: '#1a1a2e',
  // Focus-ring accent, full-opacity gold variant used for :focus-visible
  // outlines on interactive surfaces (meets 3:1 contrast on tier2 glass).
  accentRing: '#E8C84A',
  // Factor-axis palette. Canonical accents for the quantitative factor set
  // (value, growth, quality, momentum). `factorValue` / `factorMomentum`
  // intentionally alias chartBlue / chartAmber so WS-4 chart primitives and
  // WS-5 factor primitives stay visually synchronized.
  factorValue: '#60A5FA',
  factorGrowth: '#10B981',
  factorQuality: '#A78BFA',
  factorMomentum: '#F59E0B',
  // Credit-risk amber tier (BB/B/CCC) for CreditUniverse rating color.
  warning: '#F97316',
} as const

export type ColorToken = keyof typeof color

/**
 * Pillar accent maps. Maps each brand pillar to its accent color token.
 * Downstream code should prefer `pillarAccent[pillar]` over hardcoded hex.
 */
export const pillarAccent = {
  leviathan: color.gold,
  ouroboros: color.purpleBright,
  apophis: color.teal,
  crossCutting: color.slate,
} as const

export type Pillar = keyof typeof pillarAccent

/**
 * Typography scale. Sizes in px; line-height as unitless multiplier.
 * Weight and tracking tuned per role so inline styles do not need to
 * guess.
 */
export const type = {
  display: { size: 56, lineHeight: 1.05, weight: 300, tracking: '-0.02em' },
  title1: { size: 36, lineHeight: 1.15, weight: 400, tracking: '-0.01em' },
  title2: { size: 28, lineHeight: 1.2, weight: 400, tracking: '-0.005em' },
  title3: { size: 22, lineHeight: 1.25, weight: 500, tracking: '0em' },
  title4: { size: 18, lineHeight: 1.3, weight: 500, tracking: '0em' },
  body: { size: 15, lineHeight: 1.55, weight: 400, tracking: '0em' },
  bodySmall: { size: 13, lineHeight: 1.55, weight: 400, tracking: '0em' },
  caption: { size: 11, lineHeight: 1.45, weight: 500, tracking: '0.08em' },
  mono: { size: 13, lineHeight: 1.5, weight: 400, tracking: '0em' },
  monoSmall: { size: 11, lineHeight: 1.45, weight: 500, tracking: '0.02em' },
  eyebrow: { size: 10, lineHeight: 1.2, weight: 500, tracking: '0.3em' },
} as const

export type TypeToken = keyof typeof type

/**
 * Radii in px. Mirrors the Tailwind `radius-*` scale defined in
 * `src/index.css` for TS consumers.
 */
export const radius = {
  none: 0,
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  xxl: 16,
  xxxl: 20,
  pill: 9999,
} as const

export type RadiusToken = keyof typeof radius

/**
 * Spacing scale in px. Used for custom inline layout where Tailwind
 * utility classes are awkward (motion-animated gaps, computed paddings).
 */
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
} as const

export type SpaceToken = keyof typeof space

/**
 * Motion tokens: durations in ms, easing as cubic-bezier arrays suitable
 * for framer-motion `transition.ease` or CSS `transition-timing-function`.
 *
 * Easing names follow the material / Apple conventions:
 * - `standard` is the default entry-exit curve.
 * - `entrance` accelerates at the start (used for elements arriving into view).
 * - `exit` decelerates at the end (used for elements leaving).
 * - `emphasized` is the gold-standard bouncy-but-controlled curve for hero motion.
 */
export const motion = {
  duration: {
    instant: 80,
    fast: 160,
    base: 240,
    slow: 360,
    slower: 480,
  },
  ease: {
    standard: [0.4, 0.0, 0.2, 1],
    entrance: [0.0, 0.0, 0.2, 1],
    exit: [0.4, 0.0, 1, 1],
    emphasized: [0.25, 0.1, 0, 1],
    spring: [0.32, 0.72, 0, 1],
  },
  stagger: {
    tight: 0.03,
    base: 0.06,
    loose: 0.1,
  },
} as const

/**
 * Elevation tiers. Each tier maps to a backdrop-blur + rgba fill + hairline
 * border combination that composes the "glass" aesthetic. Consumers should
 * read these as CSS values, not rebuild them ad-hoc.
 *
 * `tier0` = flat surface (no elevation), used for page background.
 * `tier1` = resting card on the page.
 * `tier2` = hover / active card.
 * `tier3` = floating surface (popover, dropdown).
 * `tier4` = modal / dialog / command palette.
 */
export const elevation = {
  tier0: {
    background: 'transparent',
    border: 'transparent',
    backdropFilter: 'none',
  },
  tier1: {
    background: 'rgba(10, 10, 14, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    backdropFilter: 'blur(8px)',
  },
  tier2: {
    background: 'rgba(14, 14, 20, 0.7)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(12px)',
  },
  tier3: {
    background: 'rgba(8, 8, 12, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(20px) saturate(1.4)',
  },
  tier4: {
    background: 'rgba(4, 4, 8, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(32px) saturate(1.6)',
  },
} as const

export type ElevationTier = keyof typeof elevation

/**
 * Z-index tiers. Centralized so nothing ends up at `zIndex: 999999`.
 */
export const z = {
  base: 0,
  content: 1,
  sticky: 10,
  sidebar: 30,
  topbar: 40,
  overlay: 50,
  dropdown: 60,
  popover: 70,
  toast: 80,
  dialog: 90,
  commandPalette: 100,
} as const

export type ZToken = keyof typeof z
